import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AppNotificationToast, useAppNotificationToast } from '../../shared/components/AppNotificationToast';
import { notifyBulkDeleted, notifyCreated, notifyDeleted, notifyError, notifyUpdated } from '../../shared/utils/crudNotify';
import { sortRecordsNewestFirst } from '../../shared/utils/listSortUtils';
import { useAppData } from '../../core/contexts/AppDataContext';
import { useSettings } from '../../core/contexts/SettingsContext';
import { resolveBanqueEntityReference } from './bankingHelpers';
import { useBanqueConfig, sliceBanqueListItems } from './useBanqueConfig';
import { useTranslation } from 'react-i18next';
import { formatBrandMoney, getBrandLocale } from '../../shared/utils/brandCurrency';
import {
  SAUDI_BANKS,
  SAUDI_REGIONS,
  SAUDI_PRIMARY_BANK_CODES,
  SAUDI_IBAN_PLACEHOLDER,
  validateSaudiIban,
  formatSaudiIbanDisplay,
  normalizeSaudiIban,
  bankMatchesCode,
} from './saudiBankingDefaults';
import CustomSelect from '../../shared/components/custom/CustomSelect';
import CustomNumberInput from '../../shared/components/custom/CustomNumberInput';
import '../../shared/styles/GPSTrackingDatePicker.css';
import { exportBankingRows, exportBankingPdf, importBankingRows } from './bankingExportImport';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import {
  Building2,
  Plus,
  Calendar,
  Filter,
  Download,
  Upload,
  FileText,
  FileSpreadsheet,
  FileOutput,
  List,
  LayoutList,
  Grid,
  RefreshCcw,
  Trash2,
  Banknote,
  CheckCircle,
  XCircle,
  TrendingUp,
  X,
  Layers,
  Shield,
  LayoutGrid,
} from 'lucide-react';
import QawaemErpListPageShell, { sliceQawaemStats } from '../../products/saudi/components/QawaemErpListPageShell';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import useColumnHeaderFilters from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import {
  mergeStickyActionsClass,
  mergeStickySelectClass,
  QawaemTableStickyActionsTd,
} from '../../products/saudi/components/QawaemTableStickyActions';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';


const BankAccountsPage = () => {
  const { t, i18n } = useTranslation();
  const p = (key, opts) => t(`modules.banque.pages.bankAccountsPage.${key}`, opts);
  const dateLocale = getBrandLocale(i18n.language);
  const formatAmount = (amount) => formatBrandMoney(amount);
  const { getBankAccounts, addBankAccount, updateBankAccount, deleteBankAccount } = useAppData();
  const { settings } = useSettings();
  const bankingConfig = useBanqueConfig();
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const entityLabel = p('entity_label', { defaultValue: 'الحساب البنكي' });
  const entityLabelPlural = p('entity_label_plural', { defaultValue: 'حسابات بنكية' });
  
  // ===== STATE MANAGEMENT =====
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [selectedItems, setSelectedItems] = useState([]);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  
  // Dropdown states
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  
  // Modals
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingAccount, setViewingAccount] = useState(null);
  const [currentAccount, setCurrentAccount] = useState(null);
  
  // Filters
  const [quickFilter, setQuickFilter] = useState('all');
  const [bankFilter, setBankFilter] = useState('all');
  const [activeDistributionFilter, setActiveDistributionFilter] = useState(null);
  const [mainViewMode, setMainViewMode] = useState('all_accounts');
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [columnVisibility, setColumnVisibility] = useState({
    accountCode: true,
    name: true,
    bank: true,
    accountNumber: true,
    balance: true,
  });
  
  const [customBankMode, setCustomBankMode] = useState(false);
  const [formData, setFormData] = useState({
    accountCode: '',
    name: '',
    accountNumber: '',
    bank: '',
    address: '',
    wilaya: '',
    balance: '',
    active: true
  });

  // Loading states
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Refs
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);

  // ===== EFFECTS =====
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const modalOpen = showAddModal || showEditModal || showViewModal || showDeleteModal;
    if (!modalOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [showAddModal, showEditModal, showViewModal, showDeleteModal]);

  const rawAccounts = getBankAccounts();
  const data = useMemo(() => sortRecordsNewestFirst(rawAccounts || []), [rawAccounts]);

  const generateAccountCode = useCallback(
    () => resolveBanqueEntityReference(settings, 'bankAccount', 'BAC', data, (a) => a.accountCode),
    [settings, data],
  );

  const resolveBankLabel = useCallback((bankValue) => {
    const normalized = String(bankValue || '').trim();
    if (!normalized) return '';
    const match = SAUDI_BANKS.find((b) => b.code === normalized || b.name === normalized);
    return match?.name || normalized;
  }, []);
  
  // ===== HELPER FUNCTIONS =====
  const handleItemSelect = (id) => {
    setSelectedItems((prev) => (prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]));
  };

  // Function to create account
  const handleCreateAccount = async (accountData) => {
    setIsCreating(true);
    try {
      await addBankAccount(accountData);
      notifyCreated(showNotify, entityLabel);
      closeAccountModal();
    } catch (error) {
      notifyError(showNotify, p('toast_create_error'));
      console.error('Bank account create error:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // Function to update account
  const handleUpdateAccount = async (accountData) => {
    setIsUpdating(true);
    try {
      await updateBankAccount(accountData.id, accountData);
      notifyUpdated(showNotify, entityLabel);
      closeAccountModal();
    } catch (error) {
      notifyError(showNotify, p('toast_update_error'));
      console.error('Bank account update error:', error);
    } finally {
      setIsUpdating(false);
    }
  };

  // Function to delete account
  const handleDeleteAccount = async (id) => {
    setIsDeleting(true);
    try {
      await deleteBankAccount(id);
      notifyDeleted(showNotify, entityLabel);
      setShowDeleteModal(false);
      setCurrentAccount(null);
    } catch (error) {
      notifyError(showNotify, p('toast_delete_error'));
      console.error('Bank account delete error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(p('delete_bulk_confirm', { count: selectedItems.length }))) return;
    setIsDeleting(true);
    try {
      const deleteCount = selectedItems.length;
      await Promise.all(selectedItems.map((id) => deleteBankAccount(id)));
      setSelectedItems([]);
      notifyBulkDeleted(showNotify, deleteCount, entityLabelPlural);
    } catch (error) {
      notifyError(showNotify, p('toast_delete_error'));
      console.error('Bulk delete error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const accountExportHeaders = [
    { key: 'accountCode', label: p('col_code') },
    { key: 'name', label: p('th_account_name') },
    { key: 'bankName', label: p('th_bank') },
    { key: 'accountNumber', label: p('th_account_number') },
    { key: 'balance', label: p('th_current_balance') },
    { key: 'status', label: t('common.status') },
  ];

  const formatAccountExportStatus = (account) => {
    const isActive = account.status === 'Active' || account.active;
    return isActive ? t('common.active') : t('common.inactive');
  };

  const accountExportRows = () => (filteredAccounts || []).map((a) => ({
    accountCode: a.accountCode || '',
    name: a.description || a.name || '',
    bankName: a.bankName || a.bank || '',
    accountNumber: a.accountNumber || '',
    balance: a.balance || 0,
    status: formatAccountExportStatus(a),
  }));

  const handleExportCSV = () => { void exportBankingRows(accountExportRows(), accountExportHeaders, 'comptes_bancaires', 'csv'); setShowExportDropdown(false); };
  const handleExportExcel = () => { void exportBankingRows(accountExportRows(), accountExportHeaders, 'comptes_bancaires', 'excel'); setShowExportDropdown(false); };
  const handleExportPDF = () => { exportBankingPdf(p('title')); setShowExportDropdown(false); };
  const handleImport = () => {
    importBankingRows({
      headers: accountExportHeaders,
      schema: { requiredFields: ['name', 'accountNumber', 'bankName'], validators: { balance: (v) => !v || !Number.isNaN(parseFloat(v)) } },
      mapRow: (row) => ({
        name: row.name,
        accountNumber: row.accountNumber,
        bank: row.bankName,
        balance: row.balance || 0,
        active: row.status !== 'Inactive' && row.status !== 'inactive',
        wilaya: '',
        address: '',
      }),
      postRow: (payload) => addBankAccount(payload),
      onComplete: async () => { /* addBankAccount refreshes via context */ },
    });
    setShowImportDropdown(false);
  };

  // Function to update account status
  const handleUpdateAccountStatus = async (id, status) => {
    try {
      await updateBankAccount(id, { status, active: status === 'Active' });
      notifyUpdated(showNotify, entityLabel);
    } catch (error) {
      notifyError(showNotify, p('toast_status_error'));
      console.error('Bank account status update error:', error);
    }
  };

  const handleBankChange = (e) => {
    const selectedBank = e.target.value;
    if (selectedBank === "custom") {
      setCustomBankMode(true);
      setFormData({...formData, bank: ''});
    } else {
      setCustomBankMode(false);
      setFormData({...formData, bank: selectedBank});
    }
  };

  // Réinitialiser le formulaire
  const resetForm = () => {
    setFormData({
      accountCode: '',
      name: '',
      accountNumber: '',
      bank: '',
      address: '',
      wilaya: '',
      balance: '',
      active: true
    });
    setCustomBankMode(false);
  };

  const openAddAccountModal = () => {
    resetForm();
    setCurrentAccount(null);
    setFormData((prev) => ({ ...prev, accountCode: generateAccountCode() }));
    setShowAddModal(true);
  };

  const closeAccountModal = () => {
    setShowAddModal(false);
    setShowEditModal(false);
    setCurrentAccount(null);
    resetForm();
  };

  // Ouvrir le modal d'édition
  const handleEdit = (account) => {
    setCurrentAccount(account);

    const bankName = account.bankName || account.bank || '';
    const bankExists = SAUDI_BANKS.some(bank => bank.name === bankName || bank.code === bankName);
    setCustomBankMode(!bankExists);
    
    setFormData({
      accountCode: account.accountCode || '',
      name: account.description || account.name || '',
      accountNumber: account.accountNumber || '',
      bank: bankName,
      address: account.address || '',
      wilaya: account.wilaya || '',
      balance: account.balance || 0,
      active: account.status === 'Active' || account.active || false
    });
    setShowEditModal(true);
  };

  // Ouvrir le modal de suppression
  const handleDeleteClick = (account) => {
    setCurrentAccount(account);
    setShowDeleteModal(true);
  };

  const handleView = (account) => {
    setViewingAccount(account);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewingAccount(null);
  };

  const accountStatusOptions = useMemo(() => [
    { value: 'Active', label: t('common.active'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
    { value: 'Inactive', label: t('common.inactive'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
  ], [t]);

  const normalizeAccountStatus = (account) => (
    account?.status === 'Active' || account?.active ? 'Active' : 'Inactive'
  );

  const handleStatusChange = async (recordId, newStatus) => {
    await handleUpdateAccountStatus(recordId, newStatus);
  };

  // Gérer la soumission du formulaire
  const handleSubmit = (e) => {
    e.preventDefault();

    const ibanCheck = validateSaudiIban(formData.accountNumber);
    if (!ibanCheck.valid) {
      notifyError(showNotify, ibanCheck.message || p('toast_iban_invalid'));
      return;
    }

    const accountData = {
      ...formData,
      name: formData.name,
      bank: resolveBankLabel(formData.bank),
      accountCode: resolveBanqueEntityReference(
        settings,
        'bankAccount',
        'BAC',
        data,
        (a) => a.accountCode,
        formData.accountCode,
      ),
      accountNumber: ibanCheck.iban,
      iban: ibanCheck.iban,
      balance: parseFloat(formData.balance) || 0,
      status: formData.active ? 'Active' : 'Inactive',
    };

    if (showEditModal && currentAccount) {
      handleUpdateAccount({ id: currentAccount.id, ...accountData });
    } else {
      handleCreateAccount(accountData);
    }
  };

  // Calculate stats
  const stats = {
    total: data?.length || 0,
    active: data?.filter((acc) => acc && normalizeAccountStatus(acc) === 'Active')?.length || 0,
    inactive: data?.filter((acc) => acc && normalizeAccountStatus(acc) !== 'Active')?.length || 0,
    totalBalance: data?.reduce((sum, acc) => sum + (parseFloat(acc?.balance) || 0), 0) || 0
  };

  const qawaemStats = useMemo(() => sliceQawaemStats([
    { key: 'total', label: p('stat_total_accounts'), value: stats.total, icon: Building2, variant: 'copper' },
    { key: 'active', label: p('stat_active_accounts'), value: stats.active, icon: CheckCircle, variant: 'forest' },
    { key: 'inactive', label: p('stat_inactive_accounts'), value: stats.inactive, icon: XCircle, variant: 'copper' },
    { key: 'balance', label: p('stat_total_balance'), value: formatAmount(stats.totalBalance), icon: TrendingUp, variant: 'forest' },
  ], 4), [stats, p, formatAmount]);

  const accountColumnRules = useMemo(() => [
    textColumnRule('accountCode', (a) => a.accountCode || ''),
    textColumnRule('name', (a) => a.description || a.name || ''),
    textColumnRule('bank', (a) => a.bankName || a.bank || ''),
    textColumnRule('accountNumber', (a) => a.accountNumber || ''),
    textColumnRule('balance', (a) => String(a.balance ?? '')),
    exactColumnRule('status', (a) => (a.status === 'Active' || a.active ? t('common.active') : t('common.inactive'))),
  ], [t]);

  const filteredAccounts = useMemo(() => {
    const base = data ? data.filter((account) => {
      if (!account) return false;

      const searchLower = viewMode === 'cards' ? searchTerm.toLowerCase() : '';
      const accountName = account.description || account.name || '';
      const accountNumber = account.accountNumber || '';
      const bankName = account.bankName || account.bank || '';

      const matchesSearch = !searchLower ||
        (account.accountCode || '').toLowerCase().includes(searchLower) ||
        accountName.toLowerCase().includes(searchLower) ||
        accountNumber.toLowerCase().includes(searchLower) ||
        bankName.toLowerCase().includes(searchLower);

      const matchesBank =
        bankFilter === 'all' ||
        (account.bankName || account.bank || '').includes(bankFilter);

      const status = normalizeAccountStatus(account);
      const balance = parseFloat(account.balance) || 0;

      const matchesQuickFilter =
        quickFilter === 'all' ||
        (quickFilter === 'active' && status === 'Active') ||
        (quickFilter === 'inactive' && status === 'Inactive') ||
        (quickFilter === 'high_balance' && balance >= 100000);

      const bankNameForDist = account.bankName || account.bank || '';
      const matchesDistribution =
        !activeDistributionFilter ||
        bankMatchesCode(bankNameForDist, activeDistributionFilter);

      const matchesMainView =
        mainViewMode === 'all_accounts' ||
        mainViewMode === 'by_bank' ||
        (mainViewMode === 'by_status' && matchesQuickFilter) ||
        (mainViewMode === 'by_balance' && balance >= 100000);

      return matchesSearch && matchesBank && matchesQuickFilter && matchesDistribution && matchesMainView;
    }) : [];

    return applyColumnFilters(base, colFilters, accountColumnRules);
  }, [data, searchTerm, viewMode, bankFilter, colFilters, accountColumnRules, quickFilter, activeDistributionFilter, mainViewMode]);

  const pagedAccounts = useMemo(
    () => sliceBanqueListItems(filteredAccounts, bankingConfig.itemsPerPage),
    [filteredAccounts, bankingConfig.itemsPerPage],
  );

  const filteredAccountIds = useMemo(() => filteredAccounts.map((a) => a.id), [filteredAccounts]);
  const allRowsSelected = filteredAccountIds.length > 0 && filteredAccountIds.every((id) => selectedItems.includes(id));
  const someRowsSelected = selectedItems.length > 0 && !allRowsSelected;

  const handleSelectAll = () => {
    if (allRowsSelected || someRowsSelected) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredAccountIds);
    }
  };

  const renderAccountStatusBadge = (account, { clickable = true } = {}) => (
    <QawaemClickableStatusBadge
      status={normalizeAccountStatus(account)}
      recordId={account.id}
      options={accountStatusOptions}
      onChange={clickable ? handleStatusChange : undefined}
      title={t('common.change_status', { defaultValue: 'انقر لتغيير الحالة' })}
    />
  );

  const qawaemColumnDefs = useMemo(() => [
    { key: 'accountCode', label: p('col_code'), checked: columnVisibility.accountCode !== false, required: true },
    { key: 'name', label: p('th_account_name'), checked: columnVisibility.name !== false },
    { key: 'bank', label: p('th_bank'), checked: columnVisibility.bank !== false },
    { key: 'accountNumber', label: p('th_account_number'), checked: columnVisibility.accountNumber !== false },
    { key: 'balance', label: p('th_current_balance'), checked: columnVisibility.balance !== false },
    { key: 'actions', label: t('common.actions'), checked: true, required: true },
  ], [columnVisibility, p, t]);
  const handleToggleColumn = (key) => {
    if (key === 'actions') return;
    const col = qawaemColumnDefs.find((c) => c.key === key);
    if (col?.required) return;
    setColumnVisibility((prev) => ({ ...prev, [key]: prev[key] === false }));
  };
  const qawaemVisibleColumnCount = qawaemColumnDefs.filter((c) => c.checked !== false).length;
  const isColVisible = (key) => columnVisibility[key] !== false;
  const tableColSpan = 1 + ['accountCode', 'name', 'bank', 'accountNumber', 'balance'].filter((k) => isColVisible(k)).length + 1;

  // Bank distribution for Advanced Options
  const bankDistribution = SAUDI_PRIMARY_BANK_CODES.map((code, index) => {
    const bank = SAUDI_BANKS.find((b) => b.code === code);
    const count = data?.filter(acc => bankMatchesCode(acc.bankName || acc.bank || '', code)).length || 0;
    const colors = ['bg-blue-500', 'bg-purple-500', 'bg-green-500', 'bg-amber-500'];
    return { name: code, label: bank?.name || code, count, color: colors[index] };
  });

  // ===== RENDER =====
  return (
    <QawaemErpListPageShell
      title={p('title')}
      subtitle={p('subtitle')}
      icon={Building2}
      stats={qawaemStats}
      viewMode={viewMode}
      onViewModeChange={setViewMode}
      filterActions={selectedItems.length > 0 ? (
        <button
          type="button"
          onClick={handleBulkDelete}
          disabled={isDeleting}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-red-500/35 bg-red-500/10 text-red-700 hover:bg-red-500/20 transition-colors text-sm font-medium shrink-0"
          title={t('common.delete', { defaultValue: 'حذف' })}
        >
          <Trash2 size={16} />
          <span>{t('common.delete')} ({selectedItems.length})</span>
        </button>
      ) : null}
      searchTerm={searchTerm}
      onSearchChange={setSearchTerm}
      searchPlaceholder={p('search_placeholder', { defaultValue: 'بحث…' })}
      onCreate={openAddAccountModal}
      createLabel={p('add_button')}
      headerActions={(
        <button
          type="button"
          onClick={() => setShowAdvancedOptions(!showAdvancedOptions)}
          className={`${showAdvancedOptions ? 'bg-gradient-to-r from-blue-500 to-blue-700 border-blue-400' : 'bg-white/5 border-white/20'} border hover:bg-white/10 text-white px-4 py-3 rounded-xl font-semibold flex items-center gap-2`}
        >
          <Filter className="h-5 w-5" />
          <span className="hidden sm:inline">{t('common.advanced_options')}</span>
        </button>
      )}
      importExport={{
        importOpen: showImportDropdown,
        exportOpen: showExportDropdown,
        onToggleImport: () => { setShowImportDropdown(!showImportDropdown); setShowExportDropdown(false); },
        onToggleExport: () => { setShowExportDropdown(!showExportDropdown); setShowImportDropdown(false); },
        importRef: importDropdownRef,
        exportRef: exportDropdownRef,
        importLabel: t('common.import', { defaultValue: 'استيراد' }),
        exportLabel: t('common.export', { defaultValue: 'تصدير' }),
        onImportCSV: handleImport,
        onImportExcel: handleImport,
        onExportCSV: handleExportCSV,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      }}
    >
      {/* Advanced Options with Main Tabs, Quick Filters, and Distribution */}
      <motion.div initial={false} animate={{ height: showAdvancedOptions ? "auto" : 0, opacity: showAdvancedOptions ? 1 : 0 }} transition={{ duration: 0.3, ease: "easeInOut" }} style={{ overflow: "hidden" }}>
        <div className="space-y-4 pt-4">
          <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
            <div className="flex space-x-1 overflow-x-auto table-scroll">
              {[
                { id: 'all_accounts', nameKey: 'common.general_view', icon: LayoutGrid },
                { id: 'by_bank', nameKey: 'tab_by_bank', icon: Building2 },
                { id: 'by_status', nameKey: 'common.by_status', icon: Shield },
                { id: 'by_balance', nameKey: 'tab_by_balance', icon: TrendingUp }
              ].map((tab) => (
                <button key={tab.id} onClick={() => { setMainViewMode(tab.id); setQuickFilter('all'); setActiveDistributionFilter(null); }} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${mainViewMode === tab.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                  <tab.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{tab.nameKey.startsWith('common.') ? t(tab.nameKey) : p(tab.nameKey)}</span>
                </button>
              ))}
            </div>
          </div>

          {mainViewMode === 'all_accounts' && (
            <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
              <div className="flex space-x-1 overflow-x-auto table-scroll">
                {[
                  { id: 'all', nameKey: 'common.all', icon: Layers },
                  { id: 'active', nameKey: 'filter_active', icon: CheckCircle },
                  { id: 'inactive', nameKey: 'filter_inactive', icon: XCircle },
                  { id: 'high_balance', nameKey: 'filter_high_balance', icon: TrendingUp }
                ].map((filter) => (
                  <button key={filter.id} onClick={() => setQuickFilter(filter.id)} className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all duration-300 ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg shadow-blue-500/30' : 'text-white/80 hover:bg-white/10 hover:text-white'}`}>
                    <filter.icon className="h-5 w-5" /><span className="hidden sm:inline whitespace-nowrap">{filter.nameKey.startsWith('common.') ? t(filter.nameKey) : p(filter.nameKey)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-white">{p('distribution_title')}</h3>
              {activeDistributionFilter && (
                <button onClick={() => setActiveDistributionFilter(null)} className="text-xs text-white/60 hover:text-white bg-white/5 hover:bg-white/10 px-3 py-1.5 rounded-lg transition-all flex items-center gap-1">
                  <XCircle size={14} />{p('reset_filter')}
                </button>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              {bankDistribution.map((item) => (
                <button key={item.name} onClick={() => setActiveDistributionFilter(activeDistributionFilter === item.name ? null : item.name)} className={`bg-white/5 rounded-xl p-4 border transition-all cursor-pointer text-left ${activeDistributionFilter === item.name ? 'border-blue-400/60 bg-blue-500/20 shadow-lg shadow-blue-500/20' : 'border-white/10 hover:border-white/20 hover:bg-white/10'}`}>
                  <div className="flex justify-between items-center mb-2">
                    <h4 className={`font-medium ${activeDistributionFilter === item.name ? 'text-white' : 'text-white/80'}`}>{item.label}</h4>
                    <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white font-semibold`}>{item.count}</div>
                  </div>
                  <div className="w-full bg-white/10 h-3 rounded-full overflow-hidden">
                    <div className={`h-3 rounded-full ${item.color} transition-all`} style={{ width: `${(() => {
                      return stats.total > 0 ? (item.count / stats.total * 100).toFixed(1) : 0;
                    })()}%` }}></div>
                  </div>
                  <p className={`mt-2 text-sm ${activeDistributionFilter === item.name ? 'text-white/80' : 'text-white/60'}`}>
                    {p('percent_of_total', { percent: stats.total > 0 ? (item.count / stats.total * 100).toFixed(1) : 0 })}
                  </p>
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* Data Table with GPS Controls Bar */}
      <div className="glass-panel glass-table-shell overflow-visible">
        {viewMode === 'table' ? (
          <div className="glass-table-scroll overflow-x-auto">
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={allRowsSelected}
                        indeterminate={someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColVisible('accountCode') ? (
                    <TableColumnHeaderFilter columnKey="accountCode" label={p('col_code')} value={colFilters.accountCode || ''} onChange={(v) => setColFilter('accountCode', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('name') ? (
                    <TableColumnHeaderFilter columnKey="name" label={p('th_account_name')} value={colFilters.name || ''} onChange={(v) => setColFilter('name', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('bank') ? (
                    <TableColumnHeaderFilter columnKey="bank" label={p('th_bank')} value={colFilters.bank || ''} onChange={(v) => setColFilter('bank', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('accountNumber') ? (
                    <TableColumnHeaderFilter columnKey="accountNumber" label={p('th_account_number')} value={colFilters.accountNumber || ''} onChange={(v) => setColFilter('accountNumber', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  {isColVisible('balance') ? (
                    <TableColumnHeaderFilter columnKey="balance" label={p('th_current_balance')} value={colFilters.balance || ''} onChange={(v) => setColFilter('balance', v)} editingKey={editingKey} onEditingKeyChange={setEditingKey} thClassName="text-white" />
                  ) : null}
                  <QawaemTableHeaderActionsCell
                    label={t('common.actions')}
                    columns={qawaemColumnDefs}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumnDefs.length}
                    thClassName={mergeStickyActionsClass('px-6 py-3 text-center text-white font-semibold text-sm whitespace-nowrap align-middle')}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10 employees-table-body">
                {filteredAccounts.length > 0 ? (
                  pagedAccounts.map((account, index) => (
                    <tr key={account.id} className={`hover:bg-white/5 transition-colors ${selectedItems.includes(account.id) ? 'bg-white/10' : ''}`}>
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                        <div className="flex justify-center">
                          <QawaemTableCheckbox
                            checked={selectedItems.includes(account.id)}
                            onChange={() => handleItemSelect(account.id)}
                          />
                        </div>
                      </td>
                      {isColVisible('accountCode') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold font-mono">{account.accountCode || p('not_available')}</span></td>
                      ) : null}
                      {isColVisible('name') ? (
                        <td className="px-6 py-4"><span className="text-white font-semibold">{account.description || account.name || p('unnamed_account')}</span></td>
                      ) : null}
                      {isColVisible('bank') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm">{account.bankName || account.bank || p('unknown_bank')}</span></td>
                      ) : null}
                      {isColVisible('accountNumber') ? (
                        <td className="px-6 py-4"><span className="text-white/70 text-sm font-mono">{account.accountNumber ? formatSaudiIbanDisplay(account.accountNumber) : p('not_available')}</span></td>
                      ) : null}
                      {isColVisible('balance') ? (
                        <td className="px-6 py-4"><span className="text-white font-bold text-lg">{formatAmount(account.balance)}</span></td>
                      ) : null}
                      <QawaemTableStickyActionsTd>
                        <div className="flex items-center justify-center gap-2">
                          {renderAccountStatusBadge(account)}
                          <QawaemErpRowActions
                            onView={() => handleView(account)}
                            onEdit={() => handleEdit(account)}
                            onDelete={() => handleDeleteClick(account)}
                            viewTitle={t('common.view')}
                            editTitle={t('common.edit')}
                            deleteTitle={t('common.delete')}
                          />
                        </div>
                      </QawaemTableStickyActionsTd>
                    </tr>
                  ))
                ) : (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60"><Building2 size={48} className="mx-auto mb-4 text-white/30" /><p className="text-lg">{p('empty_title')}</p></td></tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-white/5 border-t border-white/10">
                  <td colSpan={tableColSpan} className="px-6 py-4">
                    <div className="flex items-center justify-between text-white/70 text-sm">
                      <span>{p('footer_accounts_total', { count: filteredAccounts.length })}</span>
                      <span>{p('footer_total_balance')} <span className="text-white font-semibold">{formatAmount(filteredAccounts.reduce((sum, a) => sum + (parseFloat(a.balance) || 0), 0))}</span></span>
                    </div>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {filteredAccounts.length > 0 ? (
              pagedAccounts.map((account, index) => (
                <motion.div key={account.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-4 mb-4">
                        <h3 className="text-xl font-bold text-white flex items-center gap-2"><Building2 size={20} className="text-emerald-400" />{account.description || account.name || p('unnamed_account')}</h3>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_account_info')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_bank')}</span> {account.bankName || account.bank || p('not_available')}</div>
                            <div><span className="font-medium">{p('label_account_number')}</span> {account.accountNumber || p('not_available')}</div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_balance')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium text-green-400">{p('label_current_balance')}</span> <span className="text-white font-bold text-lg">{formatAmount(account.balance)}</span></div>
                          </div>
                        </div>
                        <div>
                          <h5 className="font-medium text-white mb-2">{p('card_location')}</h5>
                          <div className="space-y-1 text-sm text-white/70">
                            <div><span className="font-medium">{p('label_wilaya')}</span> {account.wilaya || p('not_available')}</div>
                            <div><span className="font-medium">{p('label_address')}</span> {account.address || p('not_available')}</div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 ml-4">
                      {renderAccountStatusBadge(account)}
                      <QawaemErpRowActions
                        compact
                        size={14}
                        onView={() => handleView(account)}
                        onEdit={() => handleEdit(account)}
                        onDelete={() => handleDeleteClick(account)}
                        viewTitle={t('common.view')}
                        editTitle={t('common.edit')}
                        deleteTitle={t('common.delete')}
                      />
                    </div>
                  </div>
                </motion.div>
              ))
            ) : (
              <div className="text-center py-12"><Building2 size={48} className="mx-auto mb-4 text-white/30" /><p className="text-white/60 text-lg">{p('empty_title')}</p></div>
            )}
          </div>
        )}
      </div>

      {/* Add/Edit Modal — tabs (Products.js pattern) */}
      {(showAddModal || (showEditModal && currentAccount)) &&
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            style={{ top: 0, left: 0, right: 0, bottom: 0 }}
            onClick={closeAccountModal}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl max-h-[90vh] flex flex-col overflow-hidden outline-none ring-0"
              style={{
                boxShadow:
                  '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)',
                width: '90%',
                maxWidth: '1200px'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between p-6 border-b border-blue-500/20">
                <h2 className="text-2xl font-bold text-white">
                  {showEditModal ? p('modal_edit_title') : p('modal_add_title')}
                </h2>
                <div className="flex items-center gap-3">
                  <button
                    type="submit"
                    form="bank-account-form"
                    disabled={isCreating || isUpdating}
                    className="px-6 py-2.5 rounded-xl bg-gradient-to-r from-blue-500 to-blue-700 text-white hover:from-blue-600 hover:to-blue-800 transition-all duration-200 font-semibold flex items-center gap-2 shadow-lg shadow-blue-500/30 disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    <CheckCircle size={18} />
                    {showEditModal
                      ? isUpdating
                        ? p('updating')
                        : p('update')
                      : isCreating
                        ? p('creating')
                        : p('create')}
                  </button>
                  <button
                    type="button"
                    onClick={closeAccountModal}
                    className="text-white/70 hover:text-white transition-colors"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
              </div>

              <div className="overflow-y-auto p-6 flex-1 min-h-0">
                <form id="bank-account-form" onSubmit={handleSubmit}>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_code')}</label>
                      <input
                        type="text"
                        value={formData.accountCode}
                        onChange={(e) => setFormData({ ...formData, accountCode: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                        placeholder={p('placeholder_auto_generated')}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                        <RequiredLabelText text={p('label_account_name')} required />
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('placeholder_account_name')}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                        <RequiredLabelText text={p('label_account_number_form')} required />
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.accountNumber}
                        onChange={(e) => setFormData({ ...formData, accountNumber: normalizeSaudiIban(e.target.value) })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-mono"
                        placeholder={SAUDI_IBAN_PLACEHOLDER}
                      />
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">
                        <RequiredLabelText text={p('label_bank_form')} required />
                      </label>
                      {!customBankMode ? (
                        <CustomSelect
                          value={formData.bank}
                          onChange={(selectedValue) => {
                            if (selectedValue === 'custom') {
                              setCustomBankMode(true);
                              setFormData({ ...formData, bank: '' });
                            } else {
                              setFormData({ ...formData, bank: selectedValue });
                            }
                          }}
                          options={[
                            ...SAUDI_BANKS.map((bank) => ({
                              value: bank.code,
                              label: bank.name,
                              isDefault: true
                            })),
                            { value: 'custom', label: p('bank_custom_option'), isDefault: false }
                          ]}
                          placeholder={p('placeholder_type_or_select')}
                          searchable={true}
                          creatable={true}
                          autoSelect={false}
                        />
                      ) : (
                        <div className="space-y-2">
                          <input
                            type="text"
                            value={formData.bank}
                            onChange={(e) => setFormData({ ...formData, bank: e.target.value })}
                            className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                            placeholder={p('placeholder_bank_name')}
                            required
                          />
                          <button
                            type="button"
                            onClick={() => setCustomBankMode(false)}
                            className="text-sm text-blue-400 hover:text-blue-300 underline"
                          >
                            {p('back_to_bank_list')}
                          </button>
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_current_balance_sar')}</label>
                      <CustomNumberInput
                        step={0.01}
                        value={formData.balance || ''}
                        onChange={(value) => setFormData({ ...formData, balance: value })}
                        placeholder={p('placeholder_balance')}
                      />
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          id="bank-account-active"
                          checked={formData.active}
                          onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                          className="w-4 h-4 rounded border-white/20 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-white/80 text-sm font-medium">{p('label_account_active')}</span>
                      </label>
                    </div>
                    <div>
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_wilaya_detail')}</label>
                      <CustomSelect
                        value={formData.wilaya}
                        onChange={(selectedValue) => setFormData({ ...formData, wilaya: selectedValue })}
                        options={SAUDI_REGIONS.map((region) => ({
                          value: region.name,
                          label: `${region.code} - ${region.name}`,
                          isDefault: true
                        }))}
                        placeholder={p('placeholder_type_or_select')}
                        searchable={true}
                        creatable={true}
                        autoSelect={false}
                      />
                    </div>
                    <div className="md:col-span-3">
                      <label className="block w-full text-white/70 text-sm mb-2 font-medium text-right">{p('label_agency_address')}</label>
                      <input
                        type="text"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-2.5 text-white placeholder-white/30 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all"
                        placeholder={p('placeholder_agency_address')}
                      />
                    </div>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>,
          document.body
        )}

      {/* View Modal — guarded wrap (AGENTS §8) */}
      {showViewModal && viewingAccount ? (
        ReactDOM.createPortal(
          <div
            className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
            onClick={closeViewModal}
            role="presentation"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
              className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-h-[90vh] overflow-y-auto"
              style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)', width: '95%', maxWidth: '720px' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white">{p('view_modal_title')}</h2>
                <button type="button" onClick={closeViewModal} className="text-white/70 hover:text-white transition-colors">
                  <X className="h-6 w-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-white/60 text-sm mb-1">{p('label_code')}</p>
                  <p className="text-white font-medium font-mono">{viewingAccount.accountCode || p('not_available')}</p>
                </div>
                <div>
                  <p className="text-white/60 text-sm mb-1">{p('label_account_name_detail')}</p>
                  <p className="text-white font-medium text-lg">{viewingAccount.description || viewingAccount.name || p('not_available')}</p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('label_bank_detail')}</p>
                    <p className="text-white font-medium">{viewingAccount.bankName || viewingAccount.bank || p('not_available')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('label_account_number_detail')}</p>
                    <p className="text-white font-medium font-mono">{viewingAccount.accountNumber ? formatSaudiIbanDisplay(viewingAccount.accountNumber) : p('not_available')}</p>
                  </div>
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('label_current_balance_detail')}</p>
                    <p className="text-white font-bold text-xl">{formatAmount(viewingAccount.balance)}</p>
                  </div>
                </div>

                {viewingAccount.address ? (
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('label_agency_address_detail')}</p>
                    <div className="bg-white/5 rounded-lg p-3">
                      <p className="text-white font-medium">{viewingAccount.address}</p>
                    </div>
                  </div>
                ) : null}

                {viewingAccount.wilaya ? (
                  <div>
                    <p className="text-white/60 text-sm mb-1">{p('label_wilaya_detail')}</p>
                    <p className="text-white font-medium">{viewingAccount.wilaya}</p>
                  </div>
                ) : null}

                <div>
                  <p className="text-white/60 text-sm mb-1">{p('label_status_detail')}</p>
                  {renderAccountStatusBadge(viewingAccount, { clickable: false })}
                </div>
              </div>

              <button
                type="button"
                onClick={closeViewModal}
                className="w-full mt-6 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
              >
                {t('common.close')}
              </button>
            </motion.div>
          </div>,
          document.body
        )
      ) : null}

      {/* Delete Modal */}
      {showDeleteModal && currentAccount && ReactDOM.createPortal(
        <div
          className="fixed inset-0 bg-black/20 backdrop-blur-md flex items-center justify-center z-[99999] p-4"
          onClick={() => setShowDeleteModal(false)}
          role="presentation"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-gradient-to-br from-slate-800 via-blue-750 to-slate-800 backdrop-blur-lg border-2 border-blue-400/60 rounded-2xl p-6 max-w-md w-full"
            style={{ boxShadow: '0 0 40px rgba(59, 130, 246, 0.4), 0 0 80px rgba(59, 130, 246, 0.2)' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-white">{p('delete_modal_title')}</h2>
              <button type="button" onClick={() => setShowDeleteModal(false)} className="text-white/70 hover:text-white transition-colors">
                <X className="h-6 w-6" />
              </button>
            </div>
            <p className="text-white/70 mb-6">
              {p('delete_confirm', { name: currentAccount.description || currentAccount.name })}
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => handleDeleteAccount(currentAccount.id)}
                disabled={isDeleting}
                className="flex-1 bg-red-500/20 hover:bg-red-500/30 text-red-200 py-3 rounded-xl font-semibold transition-all border border-red-500/30 disabled:opacity-60"
              >
                {isDeleting ? t('common.loading') : t('common.delete')}
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 bg-white/5 hover:bg-white/10 text-white py-3 rounded-xl font-semibold transition-all border border-white/10"
              >
                {t('common.cancel')}
              </button>
            </div>
          </motion.div>
        </div>,
        document.body
      )}
      <AppNotificationToast
        visible={notifyToast.visible}
        message={notifyToast.message}
        messageHtml={notifyToast.messageHtml}
        variant={notifyToast.variant}
        status={notifyToast.status}
        showActions={notifyToast.showActions}
        topLabel={notifyToast.topLabel}
        onDismiss={dismissNotify}
        placement="bottom"
      />
    </QawaemErpListPageShell>
  );
};

export default BankAccountsPage;


